import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

/** Six keywords — the bars the frame draws. */
export const KEYWORD_ROWS = 6;

export type KeywordSentimentView = {
  /** `Top keyword` (with its mentions) and `Its negative share`. */
  kpis: KPIMetric[];
  /** Every keyword in scope, ranked by volume. The chart draws the first `KEYWORD_ROWS`. */
  rows: ChartRow[];
  xKey: string;
  /** One stack segment per sentiment present. */
  series: ChartSeries[];
  formatValue: (value: number) => string;
};

export type KeywordSentimentCardProps = WidgetBlockProps & {
  view?: KeywordSentimentView;
  /** Opens the conversations behind a bar (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `947:19576` — what each keyword's conversation is made of.
 *
 * Six keywords, normalised — the card asks what each keyword's conversation is
 * *made of*, not how big it is, which is the one question `normalize` exists
 * for. Ranked by volume all the same, so the keyword the KPI names is the top
 * row even though every bar is the same length.
 */
export function KeywordSentimentCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: KeywordSentimentCardProps) {
  const rows = view?.rows.slice(0, KEYWORD_ROWS);
  /* No series means no rows in scope — the sentiments are the ones present. */
  const hasData =
    view !== undefined &&
    rows !== undefined &&
    view.series.length > 0 &&
    !isEmpty(rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No keywords in this period"
      {...cardState(status, hasData, onRetry)}
      title="Keyword mentions and sentiment"
      helpText="The words people actually type, and how the conversation around each one splits between positive, neutral and negative."
      helpSupportingText="Every bar is scaled to full width, so this reads as composition rather than volume — what each keyword is made of, not how big it is. The rows are still ranked by volume, so the keyword the figure names is the top one."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view &&
        rows && (
          <BarChart
            data={rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            stacked
            normalize
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            /*
             * The rows hold counts; `normalize` is what turns them into shares at
             * render. Declaring the counts anyway is what keeps this correct if
             * the bars ever stop being normalised, rather than leaving a
             * percentage formatter that would then print `3900.0%`.
             */
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
